import React, { useState, useEffect } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import moment from 'moment';

import { getLastPrice, getHostName, formatCurrency } from '../../../utils/Utils';
import { ProductService } from '../../../service/ProductService';
import { normalizeMarketplaceKey } from '../../Campaign/marketplaceOperatorSettings';
import { groupSellerRows, OWN_STORES_KEY, marketplaceLabel, ownAwareMarketplaceLabel, marketplaceCurrency, operatorTextOptions, formatDelivery } from './marketplaceProfile';

// Row expansion of the marketplace operator profile: the product's sellers
// grouped by marketplace (own marketplace first), each group headed by its
// name, cheapest in-stock price and seller count. The list payload carries no
// competitors, so they are lazy-loaded on expand, as CompetitorRows does.

const MUTED = '#6b7280';

// "koctas-Usta El" -> "Usta El": the group header already names the marketplace.
// Own store rows show their marketplace instead: the seller name is the same on all of them.
const sellerName = (seller) => {
    if (seller.is_own_store && seller.marketplace_key) return marketplaceLabel(seller.marketplace_key);
    if (seller.seller_name) return seller.seller_name;
    const name = seller.name || '';
    if (seller.marketplace_key && name.includes('-')) return name.slice(name.indexOf('-') + 1);
    return name || getHostName(seller.product_url);
};

export default function MarketplaceRows({ row, t, config, settings }) {
    const [competitors, setCompetitors] = useState(null); // null = loading
    const [collapsed, setCollapsed] = useState(() => new Set());

    useEffect(() => {
        let active = true;
        const end = new Date();
        const start = moment(end).subtract(7, 'days').toDate();
        new ProductService().getCompetitors(row.id, start, end)
            .then(c => { if (active) setCompetitors(c || []); })
            .catch(() => { if (active) setCompetitors([]); });
        return () => { active = false; };
    }, [row.id]);

    if (competitors === null) {
        return <div className="pux-mp-expansion" style={{ padding: 12, textAlign: 'center' }}><ProgressSpinner style={{ width: 28, height: 28 }} strokeWidth="4" /></div>;
    }

    const grouped = groupSellerRows(competitors, config, collapsed);
    if (!grouped.length) {
        return <div className="pux-mp-expansion" style={{ color: MUTED, padding: 8 }}>{t('product.marketplace.no_sellers')}</div>;
    }

    const own = normalizeMarketplaceKey(config?.own_marketplace_key ?? '');
    // pricerest's is_first_party: a legacy parent owner (not moved to the 1P seller
    // row yet) is is_mine but not 1P. is_mine is only a fallback for an unannotated row.
    const isFirstParty = (seller) => (seller.is_first_party != null ? !!seller.is_first_party : !!seller.is_mine);

    // The buybox holder: the 1P row when it holds the buybox, otherwise the first
    // own-marketplace row whose seller name matches mp_buybox_holder.
    const holderName = (row.mp_buybox_holder || '').toLocaleLowerCase();
    const holder = row.mp_buybox_holder_is_first_party
        ? grouped.find(s => s.mpGroupKey === own && isFirstParty(s))
        : (holderName ? grouped.find(s => s.mpGroupKey === own && !isFirstParty(s)
            && sellerName(s).toLocaleLowerCase() === holderName) : null);

    const toggle = (key) => setCollapsed(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const groupLabel = (seller) => {
        if (seller.mpGroupKey === OWN_STORES_KEY) return t('product.marketplace.own_stores_group', operatorTextOptions(settings));
        if (!seller.mpGroupKey) return t('product.marketplace.other');
        if (seller.mpGroupKey === own) return `${ownAwareMarketplaceLabel(own, settings)} (${t('product.marketplace.own_marketplace')})`;
        return marketplaceLabel(seller.mpGroupKey);
    };

    const items = [];
    let currentGroup = null;
    grouped.forEach((seller, index) => {
        if (seller.mpGroup !== currentGroup) {
            currentGroup = seller.mpGroup;
            const isCollapsed = collapsed.has(seller.mpGroupKey);
            items.push(
                <div key={`g_${seller.mpGroup}`} className="preventSelect"
                     onClick={() => toggle(seller.mpGroupKey)}
                     style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', marginTop: index ? 6 : 0,
                              background: '#f3f4f6', borderRadius: 6, cursor: 'pointer', fontSize: '.85rem' }}>
                    <i className={`pi ${isCollapsed ? 'pi-chevron-right' : 'pi-chevron-down'}`} style={{ fontSize: '.7rem', color: MUTED }} />
                    <b>{groupLabel(seller)}</b>
                    {seller.mpGroupKey === OWN_STORES_KEY &&
                        <span style={{ color: MUTED, fontSize: '.78rem', fontStyle: 'italic' }}>{t('product.marketplace.own_stores_hint')}</span>}
                    <span>{seller.mpGroupMin != null ? formatCurrency(seller.mpGroupMin, marketplaceCurrency(row)) : '-'}</span>
                    {seller.mpGroupMin != null && formatDelivery(seller.mpGroupMinDelivery, t) &&
                        <span style={{ color: MUTED }} title={t('product.marketplace.delivery')}>
                            <i className="pi pi-truck" style={{ fontSize: '.72rem', marginRight: 4 }} />{formatDelivery(seller.mpGroupMinDelivery, t, seller.mpGroupDeliveryIsBuybox)}
                        </span>}
                    <span style={{ color: MUTED }}>{t('product.marketplace.n_sellers', { count: seller.mpGroupCount })}</span>
                </div>
            );
        }
        const last = getLastPrice(seller.prices);
        const inStock = !!(last && last.stock > 0);
        const firstParty = isFirstParty(seller) && seller.mpGroupKey === own;
        const delivery = inStock ? formatDelivery(last.delivery, t) : null;
        items.push(
            <div key={`s_${seller.id || seller.product_url || index}_${index}`}
                 style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 8px 4px 26px', fontSize: '.85rem',
                          background: firstParty ? '#eff6ff' : 'transparent', borderRadius: 4 }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {seller.product_url
                        ? <a href={seller.product_url} target="_blank" rel="noreferrer">{sellerName(seller)}</a>
                        : sellerName(seller)}
                </span>
                {firstParty && <span className="pux-chip pux-chip-blue">{t('product.marketplace.first_party_badge', operatorTextOptions(settings))}</span>}
                {seller === holder && <span className="pux-chip pux-chip-green"><i className="pi pi-star" /> {t('product.marketplace.holder_badge')}</span>}
                {delivery && <span className="p-text-nowrap" style={{ color: MUTED, fontSize: '.78rem' }} title={t('product.marketplace.delivery')}>
                    <i className="pi pi-truck" style={{ fontSize: '.72rem', marginRight: 4 }} />{delivery}
                </span>}
                <span className="p-text-nowrap" style={{ minWidth: 110, textAlign: 'right', color: inStock ? 'inherit' : MUTED }}>
                    {last && last.value != null ? formatCurrency(last.value, marketplaceCurrency(row)) : '-'}
                    {!inStock && <small style={{ marginLeft: 4 }}>({t('product.stock_out')})</small>}
                </span>
            </div>
        );
    });

    return <div className="pux-mp-expansion" style={{ padding: '4px 0' }}>{items}</div>;
}
